import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ClipboardList, Eye, ScrollText } from 'lucide-react';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import AdminListToolbar from '../../components/AdminListToolbar';
import {
  StandardTable,
  StandardTableBody,
  StandardTableCell,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableRow,
  EmptyTableState,
  LoadingTableState,
} from '../../../components/StandardTable';
import AdminStatCard from '../../components/AdminStatCard';
import ListPager from '../../components/ListPager';
import { AdminListResult, useAdminList } from '../../hooks/useAdminList';
import { formatDateTime } from '../../lib/format';
import { Activity as ActivityIcon, CalendarClock, UserX } from 'lucide-react';
import { PeopleIcon } from '../../../components/icons';

// ---------- Admin audit log ----------

interface AuditRow {
  id: number;
  actor_user_id: string;
  actor_display_name: string | null;
  actor_username: string | null;
  action: string;
  entity_type: string;
  entity_id: string;
  entity_label: string | null;
  changes: Record<string, unknown> | null;
  created_at: string;
}

const parseAudit = (raw: unknown): AdminListResult<AuditRow> => {
  const data = (raw as { data?: { entries?: AuditRow[]; pagination?: { total?: number; hasMore?: boolean } } }).data;
  return {
    rows: data?.entries ?? [],
    total: data?.pagination?.total ?? 0,
    hasMore: data?.pagination?.hasMore ?? false,
  };
};

// ---------- Login analytics (existing endpoint) ----------

interface LoginRow {
  id: string;
  username: string | null;
  display_name: string | null;
  email: string | null;
  organizations: string | null;
  created_at: string;
  last_login_at: string | null;
  days_since_login: number | null;
}

interface LoginSummary {
  totalUsers: number;
  active7Days: number;
  active30Days: number;
  active90Days: number;
  neverLoggedIn: number;
}

const parseLogins = (raw: unknown): AdminListResult<LoginRow> => {
  const data = (raw as { data?: { users?: LoginRow[]; pagination?: { total?: number; limit?: number; offset?: number } } }).data;
  const total = data?.pagination?.total ?? 0;
  const limit = data?.pagination?.limit ?? 0;
  const offset = data?.pagination?.offset ?? 0;
  return { rows: data?.users ?? [], total, hasMore: offset + limit < total };
};

// ---------- Simulation (impersonation) sessions ----------

interface SimulationRow {
  id: string;
  started_at: string;
  ended_at: string | null;
  ip_address: string | null;
  session_token_id: string;
  admin_name: string | null;
  admin_email: string | null;
  target_name: string | null;
  target_email: string | null;
}

const parseSimulations = (raw: unknown): AdminListResult<SimulationRow> => {
  const data = (raw as { data?: { logs?: SimulationRow[]; total?: number; limit?: number; offset?: number } }).data;
  const total = data?.total ?? 0;
  const limit = data?.limit ?? 0;
  const offset = data?.offset ?? 0;
  return { rows: data?.logs ?? [], total, hasMore: offset + limit < total };
};

const AUDIT_COLUMNS = 5;
const LOGIN_COLUMNS = 5;
const SIMULATION_COLUMNS = 5;

const ActivityView: React.FC = () => {
  const audit = useAdminList<AuditRow>({
    queryKey: 'accounts-audit-log',
    endpoint: '/admin/accounts/audit-log',
    parse: parseAudit,
    limit: 25,
    initialSort: { key: 'created_at', direction: 'desc' },
  });

  const summaryQuery = useQuery({
    queryKey: ['admin-accounts', 'login-summary'],
    queryFn: async () => {
      const response = await api.get('/admin/analytics/logins', { params: { limit: 1 } });
      return response.data.data.summary as LoginSummary;
    },
  });

  const logins = useAdminList<LoginRow>({
    queryKey: 'accounts-logins',
    endpoint: '/admin/analytics/logins',
    parse: parseLogins,
    limit: 25,
    initialSort: { key: 'last_login_at', direction: 'desc' },
    sortParamNames: { sort: 'sortBy', order: 'sortOrder' },
  });

  const simulations = useAdminList<SimulationRow>({
    queryKey: 'accounts-simulations',
    endpoint: '/admin/impersonation/logs',
    parse: parseSimulations,
    limit: 25,
    initialSort: { key: 'started_at', direction: 'desc' },
  });

  return (
    <div className="space-y-8">
      {/* ---------- Login analytics ---------- */}
      <section className="space-y-4">
        <h3 className="flex items-center gap-2 text-base font-semibold text-text-primary dark:text-text-dark-primary">
          <ActivityIcon className="h-4 w-4" />
          Login activity
        </h3>

        <div className="grid grid-cols-2 gap-4 xl:grid-cols-5">
          <AdminStatCard label="Total Users" value={summaryQuery.data?.totalUsers} icon={PeopleIcon} loading={summaryQuery.isLoading} />
          <AdminStatCard label="Active (7 days)" value={summaryQuery.data?.active7Days} icon={ActivityIcon} loading={summaryQuery.isLoading} />
          <AdminStatCard label="Active (30 days)" value={summaryQuery.data?.active30Days} icon={CalendarClock} loading={summaryQuery.isLoading} />
          <AdminStatCard label="Active (90 days)" value={summaryQuery.data?.active90Days} icon={CalendarClock} loading={summaryQuery.isLoading} />
          <AdminStatCard label="Never Logged In" value={summaryQuery.data?.neverLoggedIn} icon={UserX} loading={summaryQuery.isLoading} />
        </div>

        <AdminListToolbar
          search={logins.search}
          onSearch={logins.setSearch}
          searchPlaceholder="Search login activity by name or email…"
          searchAriaLabel="Search login activity"
        />

        {logins.isError ? (
          <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
            <AlertTriangle className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
            <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">
              Couldn't load login activity.
            </p>
            <Button variant="outline" size="sm" onClick={() => logins.refetch()}>
              Retry
            </Button>
          </div>
        ) : (
          <StandardTable horizontalScroll loading={logins.isFetching && !logins.isLoading}>
            <StandardTableHeader>
              <StandardTableRow>
                <StandardTableHeaderCell sortable sortKey="display_name" currentSortKey={logins.sortKey} sortDirection={logins.sortDirection} onSort={logins.handleSort}>
                  User
                </StandardTableHeaderCell>
                <StandardTableHeaderCell responsiveHide="md" sortable sortKey="email" currentSortKey={logins.sortKey} sortDirection={logins.sortDirection} onSort={logins.handleSort}>
                  Email
                </StandardTableHeaderCell>
                <StandardTableHeaderCell responsiveHide="lg" sortable sortKey="organizations" currentSortKey={logins.sortKey} sortDirection={logins.sortDirection} onSort={logins.handleSort}>
                  Organizations
                </StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="fit" sortable sortKey="last_login_at" currentSortKey={logins.sortKey} sortDirection={logins.sortDirection} onSort={logins.handleSort}>
                  Last Login
                </StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="fit" responsiveHide="lg" sortable sortKey="days_since_login" currentSortKey={logins.sortKey} sortDirection={logins.sortDirection} onSort={logins.handleSort}>
                  Days Since
                </StandardTableHeaderCell>
              </StandardTableRow>
            </StandardTableHeader>
            <StandardTableBody>
              {logins.isLoading ? (
                <LoadingTableState colSpan={LOGIN_COLUMNS} />
              ) : logins.rows.length === 0 ? (
                <EmptyTableState colSpan={LOGIN_COLUMNS} message="No login activity found" icon={<ActivityIcon className="h-8 w-8" />} />
              ) : (
                logins.rows.map((row) => (
                  <StandardTableRow key={row.id} hoverable={false}>
                    <StandardTableCell>
                      <span className="font-medium text-text-primary dark:text-text-dark-primary">
                        {row.display_name || row.username || '—'}
                      </span>
                    </StandardTableCell>
                    <StandardTableCell responsiveHide="md">{row.email ?? '—'}</StandardTableCell>
                    <StandardTableCell responsiveHide="lg">{row.organizations ?? '—'}</StandardTableCell>
                    <StandardTableCell>{row.last_login_at ? formatDateTime(row.last_login_at) : 'Never'}</StandardTableCell>
                    <StandardTableCell responsiveHide="lg">
                      {row.days_since_login == null ? '—' : Math.floor(row.days_since_login)}
                    </StandardTableCell>
                  </StandardTableRow>
                ))
              )}
            </StandardTableBody>
          </StandardTable>
        )}

        {!logins.isError && (
          <ListPager
            offset={logins.offset}
            limit={logins.limit}
            total={logins.total}
            page={logins.page}
            pageCount={logins.pageCount}
            hasPrev={logins.hasPrev}
            hasNext={logins.hasNext}
            onPrev={logins.prevPage}
            onNext={logins.nextPage}
            noun="users"
          />
        )}
      </section>

      {/* ---------- Simulation sessions ---------- */}
      <section className="space-y-4">
        <h3 className="flex items-center gap-2 text-base font-semibold text-text-primary dark:text-text-dark-primary">
          <Eye className="h-4 w-4" />
          Simulation sessions
        </h3>
        <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
          Every &quot;view as user&quot; session — who simulated whom, from where, and when it ended.
        </p>

        {simulations.isError ? (
          <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
            <AlertTriangle className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
            <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">
              Couldn't load simulation sessions.
            </p>
            <Button variant="outline" size="sm" onClick={() => simulations.refetch()}>
              Retry
            </Button>
          </div>
        ) : (
          <StandardTable horizontalScroll loading={simulations.isFetching && !simulations.isLoading}>
            <StandardTableHeader>
              <StandardTableRow>
                <StandardTableHeaderCell columnType="fit" sortable sortKey="started_at" currentSortKey={simulations.sortKey} sortDirection={simulations.sortDirection} onSort={simulations.handleSort}>
                  Started
                </StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="fit" sortable sortKey="ended_at" currentSortKey={simulations.sortKey} sortDirection={simulations.sortDirection} onSort={simulations.handleSort}>
                  Ended
                </StandardTableHeaderCell>
                <StandardTableHeaderCell sortable sortKey="admin" currentSortKey={simulations.sortKey} sortDirection={simulations.sortDirection} onSort={simulations.handleSort}>
                  Admin
                </StandardTableHeaderCell>
                <StandardTableHeaderCell sortable sortKey="target" currentSortKey={simulations.sortKey} sortDirection={simulations.sortDirection} onSort={simulations.handleSort}>
                  Viewed As
                </StandardTableHeaderCell>
                <StandardTableHeaderCell responsiveHide="lg" columnType="fit" sortable sortKey="ip_address" currentSortKey={simulations.sortKey} sortDirection={simulations.sortDirection} onSort={simulations.handleSort}>
                  IP
                </StandardTableHeaderCell>
              </StandardTableRow>
            </StandardTableHeader>
            <StandardTableBody>
              {simulations.isLoading ? (
                <LoadingTableState colSpan={SIMULATION_COLUMNS} />
              ) : simulations.rows.length === 0 ? (
                <EmptyTableState
                  colSpan={SIMULATION_COLUMNS}
                  message="No simulation sessions yet"
                  icon={<Eye className="h-8 w-8" />}
                />
              ) : (
                simulations.rows.map((session) => (
                  <StandardTableRow key={session.id} hoverable={false}>
                    <StandardTableCell>{formatDateTime(session.started_at)}</StandardTableCell>
                    <StandardTableCell>
                      {session.ended_at ? (
                        formatDateTime(session.ended_at)
                      ) : (
                        <span className="rounded-full bg-warning-100 px-2 py-0.5 text-xs font-medium text-warning-800 dark:bg-warning-900 dark:bg-opacity-40 dark:text-warning-300">
                          not ended
                        </span>
                      )}
                    </StandardTableCell>
                    <StandardTableCell>
                      <span className="font-medium text-text-primary dark:text-text-dark-primary">
                        {session.admin_name || session.admin_email || '—'}
                      </span>
                    </StandardTableCell>
                    <StandardTableCell>
                      {session.target_name || session.target_email || '—'}
                      {session.target_email && session.target_name && (
                        <span className="ml-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                          {session.target_email}
                        </span>
                      )}
                    </StandardTableCell>
                    <StandardTableCell responsiveHide="lg">
                      <span className="font-mono text-xs">{session.ip_address ?? '—'}</span>
                    </StandardTableCell>
                  </StandardTableRow>
                ))
              )}
            </StandardTableBody>
          </StandardTable>
        )}

        {!simulations.isError && (
          <ListPager
            offset={simulations.offset}
            limit={simulations.limit}
            total={simulations.total}
            page={simulations.page}
            pageCount={simulations.pageCount}
            hasPrev={simulations.hasPrev}
            hasNext={simulations.hasNext}
            onPrev={simulations.prevPage}
            onNext={simulations.nextPage}
            noun="sessions"
          />
        )}
      </section>

      {/* ---------- Admin audit log ---------- */}
      <section className="space-y-4">
        <h3 className="flex items-center gap-2 text-base font-semibold text-text-primary dark:text-text-dark-primary">
          <ScrollText className="h-4 w-4" />
          Admin audit log
        </h3>
        <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
          Every lifecycle action taken through the new Accounts tool — who did what, to which record, and when.
        </p>

        {audit.isError ? (
          <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
            <AlertTriangle className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
            <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">
              Couldn't load the audit log.
            </p>
            <Button variant="outline" size="sm" onClick={() => audit.refetch()}>
              Retry
            </Button>
          </div>
        ) : (
          <StandardTable horizontalScroll loading={audit.isFetching && !audit.isLoading}>
            <StandardTableHeader>
              <StandardTableRow>
                <StandardTableHeaderCell columnType="fit" sortable sortKey="created_at" currentSortKey={audit.sortKey} sortDirection={audit.sortDirection} onSort={audit.handleSort}>
                  When
                </StandardTableHeaderCell>
                <StandardTableHeaderCell sortable sortKey="actor" currentSortKey={audit.sortKey} sortDirection={audit.sortDirection} onSort={audit.handleSort}>
                  Actor
                </StandardTableHeaderCell>
                <StandardTableHeaderCell sortable sortKey="action" currentSortKey={audit.sortKey} sortDirection={audit.sortDirection} onSort={audit.handleSort}>
                  Action
                </StandardTableHeaderCell>
                <StandardTableHeaderCell sortable sortKey="entity_type" currentSortKey={audit.sortKey} sortDirection={audit.sortDirection} onSort={audit.handleSort}>
                  Record
                </StandardTableHeaderCell>
                <StandardTableHeaderCell responsiveHide="lg">Changes</StandardTableHeaderCell>
              </StandardTableRow>
            </StandardTableHeader>
            <StandardTableBody>
              {audit.isLoading ? (
                <LoadingTableState colSpan={AUDIT_COLUMNS} />
              ) : audit.rows.length === 0 ? (
                <EmptyTableState
                  colSpan={AUDIT_COLUMNS}
                  message="No admin actions recorded yet — entries appear as lifecycle actions are taken in this tool"
                  icon={<ClipboardList className="h-8 w-8" />}
                />
              ) : (
                audit.rows.map((entry) => (
                  <StandardTableRow key={entry.id} hoverable={false}>
                    <StandardTableCell>{formatDateTime(entry.created_at)}</StandardTableCell>
                    <StandardTableCell>
                      {entry.actor_display_name || entry.actor_username || entry.actor_user_id}
                    </StandardTableCell>
                    <StandardTableCell>
                      <span className="font-mono text-xs">{entry.action}</span>
                    </StandardTableCell>
                    <StandardTableCell>
                      <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">{entry.entity_type}</span>{' '}
                      {entry.entity_label || entry.entity_id}
                    </StandardTableCell>
                    <StandardTableCell responsiveHide="lg">
                      {entry.changes ? (
                        <span className="break-all font-mono text-xs text-text-secondary dark:text-text-dark-secondary">
                          {JSON.stringify(entry.changes)}
                        </span>
                      ) : (
                        '—'
                      )}
                    </StandardTableCell>
                  </StandardTableRow>
                ))
              )}
            </StandardTableBody>
          </StandardTable>
        )}

        {!audit.isError && (
          <ListPager
            offset={audit.offset}
            limit={audit.limit}
            total={audit.total}
            page={audit.page}
            pageCount={audit.pageCount}
            hasPrev={audit.hasPrev}
            hasNext={audit.hasNext}
            onPrev={audit.prevPage}
            onNext={audit.nextPage}
            noun="entries"
          />
        )}
      </section>
    </div>
  );
};

export default ActivityView;
